/**
 * Shared react-grid-layout wrapper for report compositions. Both the admin
 * canvas editor and the read-only on-screen renderer place their cells through
 * this one component, so they use identical grid math (cols / rowHeight / margin
 * / compaction). The only difference between the two is the `interactive` flag:
 *
 *   - interactive → drag + resize on, changes reported via `onLayoutChange`
 *     (merged back into `config.sections[].layout` by the caller);
 *   - static → drag + resize off, handles hidden (see `report-grid.css`), used
 *     for the finished-report preview and the geometry-aware screen renderer.
 *
 * Width is measured by RGL's `WidthProvider`, so a single fixed-`cols` grid
 * simply reflows to the container width — we deliberately do NOT use RGL's
 * responsive breakpoints, which would fork the stored geometry.
 */
import React, { useMemo } from 'react';
import RGL, { WidthProvider, type Layout } from 'react-grid-layout';
import type { SectionLayout } from '../../../support/reportView/types';
import { GRID_COLS, GRID_ROW_HEIGHT } from '../../../support/reportView/layout';
import './report-grid.css';

const GridLayout = WidthProvider(RGL);

export type ReportGridItem = {
  id: string;
  layout: SectionLayout;
  minW?: number;
  minH?: number;
  content: React.ReactNode;
};

type Props = {
  items: ReportGridItem[];
  cols?: number;
  rowHeight?: number;
  interactive?: boolean;
  /** Class on the draggable strip within each cell (interactive only). */
  draggableHandle?: string;
  /** Selector for inner controls that must not start a drag. */
  draggableCancel?: string;
  /** Called with the full set of placements after a drag/resize settles. */
  onLayoutChange?: (byId: Record<string, SectionLayout>) => void;
};

const ReportGrid: React.FC<Props> = ({
  items,
  cols = GRID_COLS,
  rowHeight = GRID_ROW_HEIGHT,
  interactive = false,
  draggableHandle = '.report-cell-drag-handle',
  draggableCancel,
  onLayoutChange,
}) => {
  const layout: Layout[] = useMemo(
    () =>
      items.map((it) => ({
        i: it.id,
        x: it.layout.x,
        y: it.layout.y,
        w: it.layout.w,
        h: it.layout.h,
        minW: it.minW ?? 1,
        minH: it.minH ?? 1,
        maxW: cols,
      })),
    [items, cols],
  );

  const handleStop = (next: Layout[]) => {
    if (!onLayoutChange) return;
    const byId: Record<string, SectionLayout> = {};
    next.forEach((l) => {
      byId[String(l.i)] = { x: l.x, y: l.y, w: l.w, h: l.h };
    });
    onLayoutChange(byId);
  };

  return (
    <GridLayout
      className={`report-grid${interactive ? '' : ' report-grid--static'}`}
      layout={layout}
      cols={cols}
      rowHeight={rowHeight}
      margin={[16, 16] as any}
      containerPadding={[0, 0]}
      compactType="vertical"
      isBounded
      isDraggable={interactive}
      isResizable={interactive}
      draggableHandle={interactive ? draggableHandle : undefined}
      draggableCancel={draggableCancel}
      onDragStop={interactive ? handleStop : undefined}
      onResizeStop={interactive ? handleStop : undefined}
    >
      {items.map((it) => (
        <div key={it.id} data-grid={layout.find((l) => l.i === it.id)}>
          {it.content}
        </div>
      ))}
    </GridLayout>
  );
};

export default ReportGrid;
